// @ts-check
"use strict"

import React from "react"
import { useSelector } from "react-redux";
import { useChatImage } from "../services/chat-provider/useChatImage";
import { getFileExtension, MimeTypes } from "@minohealth/common/utils/files";

/**
 * @typedef {Object} ExtraChatImageProps
 * @property {ChatImage} [srcImage]
 * @property {boolean} [thumbnail]
 * @property {string} [imageUrl] // @deprecated
 */

/**
 * @param {Omit<React.ComponentProps<"img">, "src"> & ExtraChatImageProps} props
 */
export default function ChatImage({ srcImage, imageUrl: srcImageUrl, thumbnail: isThumbnail = false, ...props }) {
  const imageParams = (() => {
    if(srcImageUrl) {
      return {
        thumbnailUrl: srcImageUrl,
        thumbnailFile: null,
        imageUrl: srcImageUrl,
        imageFile: null,
      }
    }

    if(srcImage?.type == null) {
      return {
        thumbnailUrl: null,
        thumbnailFile: null,
        imageUrl: null,
        imageFile: null,
      }
    }

    let imageMimeType = srcImage.type
    if (imageMimeType === MimeTypes.OCTET_STREAM) {
      const imageFileExtension = getFileExtension(srcImage.resourceUrl ?? "")
      if(["jpeg", "jpg"].includes(imageFileExtension)) imageMimeType = MimeTypes.JPEG
      if(imageFileExtension === "png") imageMimeType = MimeTypes.PNG
      if(["dcm", "dicom"].includes(imageFileExtension)) imageMimeType = MimeTypes.DICOM

      console.error("Unsupported file type", { imageMimeType, imageFileExtension });
      return null
    }

    if(imageMimeType === MimeTypes.PNG || imageMimeType === MimeTypes.JPEG) {
      return {
        thumbnailUrl: srcImage.thumbnailUrl ?? srcImage.resourceUrl,
        thumbnailFile: srcImage.localFile,
        imageUrl: srcImage.resourceUrl,
        imageFile: srcImage.localFile,
      }
    }

    if(imageMimeType === MimeTypes.DICOM) {
      return {
        thumbnailUrl: srcImage.thumbnailUrl,
        thumbnailFile: null,
        imageUrl: null,
        imageFile: null,
      }
    }

    console.error(`Unsupported image type "${imageMimeType}"`)
    return null
  })()

  const { user } = useSelector((state) => state.app.datastate)
  const { thumbnailUrl, thumbnailFile, imageUrl, imageFile } = imageParams ?? {}

  const {
    previewUrl,
    isError,
    isLoading,
  } = useChatImage(
    isThumbnail
      ? { url: thumbnailUrl ?? undefined, localFile: thumbnailFile ?? undefined, userToken: user?.userToken }
      : { url: imageUrl ?? undefined, localFile: imageFile ?? undefined, userToken: user?.userToken }
  )

  if(imageParams == null) {
    return <img {...props} src={DEFAULT_IMAGE_PREVIEW_SRC} />
  }

  if(isLoading || isError) {
    return <img {...props} src={DEFAULT_IMAGE_PREVIEW_SRC} />
  }

  return <img {...props} src={previewUrl} />
}

export const DEFAULT_IMAGE_PREVIEW_SRC = "https://minohealth-storage.fra1.cdn.digitaloceanspaces.com/chat_placeholder_img.jpg";
